import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { darkTheme, lightTheme, type Theme } from '@crm/design-tokens';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ColorScheme = 'light' | 'dark';

/** Misma clave que lee el script anti-destello de `index.html`. */
const STORAGE_KEY = 'crm.theme';

interface ThemeContextValue {
  /** Lo que eligió la persona: claro, oscuro o «lo que diga el sistema». */
  preference: ThemePreference;
  /** Lo que se está pintando de verdad ahora mismo. */
  scheme: ColorScheme;
  /** Tokens crudos del esquema activo, para Recharts y demás SVG. */
  tokens: Theme;
  setPreference: (value: ThemePreference) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readPreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  } catch {
    // localStorage puede estar bloqueado (modo privado, políticas de empresa).
  }
  return 'system';
}

function systemScheme(): ColorScheme {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [system, setSystem] = useState<ColorScheme>(systemScheme);

  // Seguimos el ajuste del sistema en vivo: si la persona eligió «sistema» y
  // su equipo cambia a oscuro al anochecer, la app cambia con él.
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystem(event.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const scheme: ColorScheme = preference === 'system' ? system : preference;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', scheme === 'dark');
    root.classList.toggle('light', scheme !== 'dark');
    root.style.colorScheme = scheme;
  }, [scheme]);

  const setPreference = useCallback((value: ThemePreference) => {
    setPreferenceState(value);
    try {
      // `system` también se guarda: distinguir «no ha elegido» de «eligió
      // seguir al sistema» importa si algún día cambia el valor por defecto.
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Sin persistencia el tema sigue funcionando durante la sesión.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      scheme,
      tokens: scheme === 'dark' ? darkTheme : lightTheme,
      setPreference,
      toggle: () => setPreference(scheme === 'dark' ? 'light' : 'dark'),
    }),
    [preference, scheme, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme debe usarse dentro de <ThemeProvider>');
  return ctx;
}

/** Atajo para los gráficos, que necesitan valores crudos y no clases. */
export function useChartTokens() {
  const { tokens } = useTheme();
  return tokens.chart;
}
